import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { ChartOptions } from 'chart.js';
import moment from 'moment';
import { BaseChartDirective } from 'ng2-charts';

import { ApiService } from 'src/services/api.service';

interface MonitoringMetric {
  type: string;
  data: Array<Array<{ timestamp: string; value: number }>>;
}

@Component({
  selector: 'app-monitoring',
  templateUrl: './monitoring.component.html',
  styleUrls: ['./monitoring.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, BaseChartDirective],
})
export class MonitoringComponent implements OnInit {
  isLoading = false;
  startTime: Date;
  endTime: Date;

  // API Request Latency Chart
  apiLatencyChartData: any = {
    labels: [],
    datasets: [],
  };

  // Queue Processing Latency Chart
  queueLatencyChartData: any = {
    labels: [],
    datasets: [],
  };

  apiLatencyChartOptions: ChartOptions = {
    responsive: true,
    animation: { duration: 600 },
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: 'API Request Latency (seconds)',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(3) + 's';
            }
            return label;
          },
        },
      },
      decimation: {
        enabled: true,
        algorithm: 'lttb',
        samples: 100,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 12,
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Latency (seconds)',
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        hitRadius: 5,
        hoverRadius: 4,
      },
      line: {
        borderWidth: 2,
      },
    },
  };

  queueLatencyChartOptions: ChartOptions = {
    responsive: true,
    animation: { duration: 600 },
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: 'Queue Processing Latency (seconds)',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(1) + 's';
            }
            return label;
          },
        },
      },
      decimation: {
        enabled: true,
        algorithm: 'lttb',
        samples: 100,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 12,
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Oldest Unacked Message Age (seconds)',
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        hitRadius: 5,
        hoverRadius: 4,
      },
      line: {
        borderWidth: 2,
      },
    },
  };

  constructor(private apiService: ApiService) {
    // Default to last 24 hours
    this.endTime = new Date();
    this.startTime = new Date(this.endTime.getTime() - 24 * 60 * 60 * 1000);
  }

  ngOnInit() {
    this.loadMetrics();
  }

  async loadMetrics() {
    this.isLoading = true;

    try {
      const metrics: MonitoringMetric[] = await this.apiService.postPromise('/monitoring/metrics', {
        startTime: this.startTime.toISOString(),
        endTime: this.endTime.toISOString(),
      });

      this.processApiLatencyMetrics(metrics);
      this.processQueueLatencyMetrics(metrics);
    } catch (error) {
      console.error('Error loading monitoring metrics:', error);
    } finally {
      this.isLoading = false;
    }
  }

  processApiLatencyMetrics(metrics: MonitoringMetric[]) {
    // Extract the three API latency percentile metrics
    const p99Metric = metrics.find((m) => m.type === 'API_REQUEST_LATENCY_PERCENTILE_99');
    const p95Metric = metrics.find((m) => m.type === 'API_REQUEST_LATENCY_PERCENTILE_95');
    const p50Metric = metrics.find((m) => m.type === 'API_REQUEST_LATENCY_PERCENTILE_50');

    // Combine all data points with timestamps
    const allDataPoints: Array<{ timestamp: Date; p99?: number; p95?: number; p50?: number }> = [];
    const timestampMap = new Map<number, { p99?: number; p95?: number; p50?: number }>();

    // Process P99
    if (p99Metric?.data?.[0]) {
      p99Metric.data[0].forEach((point) => {
        const time = new Date(point.timestamp).getTime();
        if (!timestampMap.has(time)) {
          timestampMap.set(time, {});
        }
        timestampMap.get(time)!.p99 = point.value / 1000; // Convert from milliseconds to seconds
      });
    }

    // Process P95
    if (p95Metric?.data?.[0]) {
      p95Metric.data[0].forEach((point) => {
        const time = new Date(point.timestamp).getTime();
        if (!timestampMap.has(time)) {
          timestampMap.set(time, {});
        }
        timestampMap.get(time)!.p95 = point.value / 1000; // Convert from milliseconds to seconds
      });
    }

    // Process P50
    if (p50Metric?.data?.[0]) {
      p50Metric.data[0].forEach((point) => {
        const time = new Date(point.timestamp).getTime();
        if (!timestampMap.has(time)) {
          timestampMap.set(time, {});
        }
        timestampMap.get(time)!.p50 = point.value / 1000; // Convert from milliseconds to seconds
      });
    }

    // Convert to sorted array
    const sortedData = Array.from(timestampMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([timestamp, values]) => ({
        timestamp: new Date(timestamp),
        ...values,
      }));

    // Always show date and time
    const dateFormat = 'MMM D, HH:mm';

    this.apiLatencyChartData = {
      labels: sortedData.map((d) => moment(d.timestamp).format(dateFormat)),
      datasets: [
        {
          label: 'P99',
          data: sortedData.map((d) => d.p99 ?? null),
          borderColor: '#cc0022',
          backgroundColor: 'rgba(204, 0, 34, 0.05)',
          tension: 0.3,
          fill: false,
        },
        {
          label: 'P95',
          data: sortedData.map((d) => d.p95 ?? null),
          borderColor: '#cc9922',
          backgroundColor: 'rgba(204, 153, 34, 0.05)',
          tension: 0.3,
          fill: false,
        },
        {
          label: 'P50 (Median)',
          data: sortedData.map((d) => d.p50 ?? null),
          borderColor: '#00cc22',
          backgroundColor: 'rgba(0, 204, 34, 0.05)',
          tension: 0.3,
          fill: false,
        },
      ],
    };
  }

  processQueueLatencyMetrics(metrics: MonitoringMetric[]) {
    // Extract the queue metrics
    // const criticalPriority = metrics.find((m) => m.type === 'QUEUE_PROCESSING_LATENCY_CRITICAL_PRIORITY');
    // const lowPriority = metrics.find((m) => m.type === 'QUEUE_PROCESSING_LATENCY_LOW_PRIORITY');
    const slackEvents = metrics.find((m) => m.type === 'QUEUE_PROCESSING_LATENCY_SLACK_EVENTS');
    const slackMessages = metrics.find((m) => m.type === 'QUEUE_PROCESSING_LATENCY_SLACK_MESSAGES');

    // Combine all data points with timestamps
    const timestampMap = new Map<number, { critical?: number; low?: number; events?: number; messages?: number }>();

    // Process each queue
    // if (criticalPriority?.data?.[0]) {
    //   criticalPriority.data[0].forEach((point) => {
    //     const time = new Date(point.timestamp).getTime();
    //     if (!timestampMap.has(time)) {
    //       timestampMap.set(time, {});
    //     }
    //     timestampMap.get(time)!.critical = point.value;
    //   });
    // }

    // if (lowPriority?.data?.[0]) {
    //   lowPriority.data[0].forEach((point) => {
    //     const time = new Date(point.timestamp).getTime();
    //     if (!timestampMap.has(time)) {
    //       timestampMap.set(time, {});
    //     }
    //     timestampMap.get(time)!.low = point.value;
    //   });
    // }

    if (slackEvents?.data?.[0]) {
      slackEvents.data[0].forEach((point) => {
        const time = new Date(point.timestamp).getTime();
        if (!timestampMap.has(time)) {
          timestampMap.set(time, {});
        }
        timestampMap.get(time)!.events = point.value;
      });
    }

    if (slackMessages?.data?.[0]) {
      slackMessages.data[0].forEach((point) => {
        const time = new Date(point.timestamp).getTime();
        if (!timestampMap.has(time)) {
          timestampMap.set(time, {});
        }
        timestampMap.get(time)!.messages = point.value;
      });
    }

    // Convert to sorted array
    const sortedData = Array.from(timestampMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([timestamp, values]) => ({
        timestamp: new Date(timestamp),
        ...values,
      }));

    // Always show date and time
    const dateFormat = 'MMM D, HH:mm';

    this.queueLatencyChartData = {
      labels: sortedData.map((d) => moment(d.timestamp).format(dateFormat)),
      datasets: [
        // {
        //   label: 'Critical Priority Tasks',
        //   data: sortedData.map((d) => d.critical ?? null),
        //   borderColor: '#cc0022',
        //   backgroundColor: 'rgba(204, 0, 34, 0.05)',
        //   tension: 0.3,
        //   fill: false,
        // },
        // {
        //   label: 'Low Priority Tasks',
        //   data: sortedData.map((d) => d.low ?? null),
        //   borderColor: '#0088cc',
        //   backgroundColor: 'rgba(0, 136, 204, 0.05)',
        //   tension: 0.3,
        //   fill: false,
        // },
        {
          label: 'Slack Events',
          data: sortedData.map((d) => d.events ?? null),
          borderColor: '#cc9922',
          backgroundColor: 'rgba(204, 153, 34, 0.05)',
          tension: 0.3,
          fill: false,
        },
        {
          label: 'Slack Messages',
          data: sortedData.map((d) => d.messages ?? null),
          borderColor: '#9922cc',
          backgroundColor: 'rgba(153, 34, 204, 0.05)',
          tension: 0.3,
          fill: false,
        },
      ],
    };
  }

  setTimeRange(hours: number) {
    this.endTime = new Date();
    this.startTime = new Date(this.endTime.getTime() - hours * 60 * 60 * 1000);
    this.loadMetrics();
  }
}
